/**
 * The languages a user can pick. Only these are applied to the UI; any other
 * stored value (de, es, fr, it, pt, or a locale such as de_DE) falls back to
 * English because those translation files are not maintained.
 */
export const SELECTABLE_LANGUAGES = [
  // Each language is shown by its own name (endonym) and is never translated,
  // so a user can find their language whatever the UI is set to.
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' },
] as const;

export type SelectableLanguage = typeof SELECTABLE_LANGUAGES[number]['code'];

export const DEFAULT_LANGUAGE: SelectableLanguage = 'en';

/** localStorage key for the last applied language (not a secret). */
export const LANGUAGE_STORAGE_KEY = 'tara_language';

/**
 * Maps any stored language value ("nl", "NL", "nl_NL", "nl-BE", "de", null)
 * to a selectable language, defaulting to English.
 */
export function normaliseLanguage(value: unknown): SelectableLanguage {
  if (typeof value !== 'string') return DEFAULT_LANGUAGE;
  const base = value.trim().toLowerCase().split(/[_-]/)[0];
  const match = SELECTABLE_LANGUAGES.find((lang) => lang.code === base);
  return match ? match.code : DEFAULT_LANGUAGE;
}

/** The language remembered from the last session, normalised. */
export function getRememberedLanguage(): SelectableLanguage {
  try {
    return normaliseLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

/** Whether a language was remembered from an earlier session. */
export function hasRememberedLanguage(): boolean {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Drops the remembered language, so the next session on this browser starts
 * from its own settings instead of the previous user's language.
 */
export function forgetLanguage(): void {
  try {
    window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // storage unavailable; nothing was remembered
  }
}

export function rememberLanguage(value: unknown): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normaliseLanguage(value));
  } catch {
    // storage unavailable (private mode); the server value still applies
  }
}
